import { Link } from 'react-router-dom'

/**
 * プライバシーポリシー（公開ページ・認証不要）。
 * App Store審査およびApp Privacyラベルの整合のため、収集データと利用目的・共有先・
 * 保持と削除・国際移転・お問い合わせ先を明示する。
 * 注意: 具体的なAI/音声認識ベンダー名は記載せず「外部のクラウドサービス事業者」と総称する。
 * 事業者名・連絡先は事業実態に合わせて維持すること。
 */
export function PrivacyPolicyPage() {
  return (
    <div className="flex-1 w-full overflow-y-auto" style={{ background: 'var(--color-surface)' }}>
      <div className="w-full px-6 py-10" style={{ maxWidth: 820, margin: '0 auto', lineHeight: 1.9 }}>
        <div className="mb-6">
          <Link to="/login" className="text-sm text-secondary">← 戻る</Link>
        </div>
        <h1 className="text-2xl font-semibold" style={{ marginBottom: 8 }}>プライバシーポリシー</h1>
        <p className="text-sm text-secondary" style={{ marginBottom: 8 }}>最終更新日: 2026年7月</p>
        <p className="text-sm" style={{ marginBottom: 24 }}>
          株式会社ブループリント（以下「当社」）は、当社が提供する議事録プラットフォーム「BluePaper」（以下「本サービス」）の
          利用にあたり取得する利用者の個人情報を、本ポリシーに従って適切に取り扱います。本サービスを利用することで、
          利用者は本ポリシーに記載された取り扱いに同意したものとみなします。
        </p>

        <Section title="1. 取得する情報">
          当社は、本サービスの提供にあたり以下の情報を取得します。
          <List items={[
            'アカウント情報：氏名（表示名）、メールアドレス、プロフィール画像。GoogleまたはAppleでのサインインを利用する場合、認証事業者から提供されるこれらの情報を取得します。',
            '会議コンテンツ：会議の音声・動画データ、および文字起こし・議事録・要約などの生成コンテンツ。',
            'カレンダー情報：カレンダー連携を有効にした場合、会議の予定情報（タイトル・日時・参加者）。',
            '利用状況・技術情報：機能の利用回数、処理時間などの統計情報、端末・ブラウザに関する情報、IPアドレス、ログ情報。',
            '決済情報：有料プランを利用する場合、決済代行事業者を通じて処理される請求に必要な情報（当社はカード番号自体を保持しません）。',
            'お問い合わせ内容：利用者が当社に送信したメールやフォームに含まれる情報。',
          ]} />
        </Section>

        <Section title="2. 利用目的">
          取得した情報は、以下の目的で利用します。
          <List items={[
            '本サービスの提供（アカウント管理、文字起こし・議事録の生成、共有・権限管理など）。',
            '本サービスの維持・改善、品質向上、不具合対応、および利用状況の分析。',
            'サポート・お問い合わせへの対応。',
            '不正利用の防止、セキュリティの確保。',
            '法令に基づく対応、および利用規約の履行。',
          ]} />
        </Section>

        <Section title="3. 第三者への提供・委託">
          当社は、法令に基づく場合を除き、本人の同意なく個人情報を第三者に販売・提供しません。ただし、
          本サービスの提供に必要な範囲で、以下の事業者に対して業務を委託し、または情報を送信することがあります。
          <List items={[
            'クラウドインフラ・ホスティング・認証を提供する事業者（Google Cloud / Firebase 等）。',
            '音声認識・AI処理を提供する外部のクラウドサービス事業者（国内外）。会議の音声・動画および文字起こしは、文字起こし・要約等の処理を行う目的でこれらの事業者に送信されます。処理はサービス提供に必要な範囲に限定され、当該事業者による広告目的等での利用は行われません。',
            '決済代行事業者（有料プランを利用する場合）。',
            'アクセス解析・監視を提供する事業者。',
          ]} />
          Google APIから取得した情報の利用および他アプリへの移転は、Google API Services User Data Policy
          （Limited Use 要件を含む）に準拠します。
        </Section>

        <Section title="4. 国外への移転">
          前項の音声認識・AI処理等の一部は、日本国外に所在する事業者によって提供される場合があります。この場合、
          利用者の情報は当該事業者の所在国に移転され、処理されることがあります。当社は、移転先の事業者が適切な
          安全管理措置を講じるよう合理的な努力を行います。
        </Section>

        <Section title="5. データの保持と削除">
          当社は、利用者が本サービスのアカウントを保有している間、または利用目的の達成に必要な期間、個人情報を保持します。
          法令上の義務の履行、紛争解決等のために必要な場合は、これを超えて保持することがあります。
          <br /><br />
          利用者は、アプリ内の「<strong>設定 → プロフィール → アカウントを削除</strong>」から、いつでもアカウントと
          本人に紐づく個人情報の削除を請求できます。削除後、本人に紐づく個人情報は速やかに削除されます。
          削除に関するお問い合わせは、本ポリシー末尾の連絡先までご連絡ください。
        </Section>

        <Section title="6. Cookie等の利用">
          本サービスは、ログイン状態の保持や設定の記憶、利用状況の分析のために、Cookieおよび類似技術を利用します。
          利用者はブラウザの設定によりCookieを無効化できますが、その場合、本サービスの一部機能が利用できなくなることがあります。
        </Section>

        <Section title="7. 安全管理措置">
          当社は、個人情報への不正アクセス、紛失、破壊、改ざん、漏えい等を防止するため、通信の暗号化、アクセス制御、
          監査ログの取得等、適切な物理的・技術的・組織的な安全管理措置を講じます。
        </Section>

        <Section title="8. 利用者の権利">
          利用者は、当社が保有する自己の個人情報について、開示・訂正・利用停止・削除等を請求することができます。
          一部の情報はアカウントにログインして直接確認・変更できます。その他の請求については、本ポリシー末尾の連絡先まで
          ご連絡ください。
        </Section>

        <Section title="9. 未成年者の情報">
          本サービスは、13歳未満の方を対象としていません。13歳未満の方は本サービスに登録しないでください。
          13歳未満の方の個人情報を取得したことが判明した場合、当社は速やかに当該情報を削除します。
        </Section>

        <Section title="10. 本ポリシーの変更">
          当社は、必要に応じて本ポリシーを変更することがあります。重要な変更を行う場合は、本サービス上での掲示等
          適切な方法で通知します。変更後に本サービスを利用した場合、変更後のポリシーに同意したものとみなします。
        </Section>

        <Section title="11. お問い合わせ">
          本ポリシーに関するお問い合わせ、および個人情報の取り扱いに関するご請求は、以下までご連絡ください。
          <br /><br />
          事業者名: 株式会社ブループリント<br />
          お問い合わせ: <a href="https://bp-g.jp/contact" style={{ textDecoration: 'underline' }}>https://bp-g.jp/contact</a>
        </Section>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 className="text-lg font-semibold" style={{ marginBottom: 8 }}>{title}</h2>
      <div className="text-sm">{children}</div>
    </section>
  )
}

function List({ items }: { items: string[] }) {
  return (
    <ul style={{ listStyle: 'disc', paddingLeft: 20, marginTop: 8 }}>
      {items.map((it, i) => (
        <li key={i} style={{ marginBottom: 6 }}>{it}</li>
      ))}
    </ul>
  )
}
